/**
 * «Добавить сотрудника» — человек, который ещё не писал боту.
 *
 * До 29.09.2026 завести его с экрана было нельзя вовсе. Функция
 * `create_employee` существовала с первой миграции, но звали её только
 * двое — одобрение заявки на доступ и первоначальная настройка владельца,
 * — а штат заводился скриптом с машины разработчика. Владелец сказал
 * прямо: «сейчас я сам там этого делать не могу».
 *
 * Между тем человек нужен в справочнике задолго до доступа: его ставят в
 * табель (D214) и за ним закрепляют код официанта из кассы (D237). Заявка
 * на доступ, когда она придёт, привяжется к уже заведённому (D234) — и
 * именно ради этого порядок такой, а не обратный.
 *
 * **Тёзка не заводится молча.** Совпадение имён чаще означает, что того
 * же человека заводят второй раз, чем что их двое: у одного окажутся
 * смены в табеле, у другого — доступ и код кассы, и разошедшиеся
 * двойники будут стоить дороже, чем лишний вопрос. Поэтому сервер
 * отказывает, называя того, кто уже есть, а согласие завести ещё одного
 * даётся отдельным нажатием.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { TEAM_KEYS, problemText, teamApi } from './api';

export function AddStaff({ positions }: { positions: { id: number; name: string; is_owner: boolean }[] }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [positionId, setPositionId] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Сервер сказал «такой уже есть». Второе нажатие подтверждает. */
  const [twin, setTwin] = useState(false);

  const add = useMutation<{ full_name: string }, ApiError, boolean>({
    mutationFn: (allowDuplicate) => teamApi.addStaff(name.trim(), positionId, allowDuplicate),
    onSuccess: () => {
      setName('');
      setPositionId(null);
      setProblem(null);
      setTwin(false);
      setOpen(false);
      client.invalidateQueries({ queryKey: TEAM_KEYS.users });
    },
    onError: (error) => {
      // 409 — не отказ, а вопрос: «это точно другой человек?»
      setTwin(error.status === 409);
      setProblem(problemText(error));
    },
  });

  if (!open) {
    return (
      <button type="button" className="button" onClick={() => setOpen(true)}>
        Добавить сотрудника
      </button>
    );
  }

  return (
    <form
      className="addstaff"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim().length < 2 || add.isPending) return;
        add.mutate(twin);
      }}
    >
      <label className="addstaff__field">
        <span className="sr-only">Имя и фамилия</span>
        <input
          className="input"
          type="text"
          value={name}
          placeholder="Имя и фамилия"
          aria-label="Имя и фамилия"
          maxLength={120}
          onChange={(event) => {
            setName(event.target.value);
            // Имя поменяли — прежнее согласие на тёзку больше не про него.
            setTwin(false);
            setProblem(null);
          }}
        />
      </label>
      <label className="addstaff__field">
        <span className="sr-only">Должность</span>
        <select
          className="input"
          aria-label="Должность"
          value={positionId ?? ''}
          onChange={(event) => setPositionId(event.target.value ? Number(event.target.value) : null)}
        >
          <option value="">Без должности</option>
          {positions
            // «Собственник» не назначается заранее: вместе с должностью
            // человеку открывается вход во все агенты, а входа у него ещё нет.
            .filter((position) => !position.is_owner)
            .map((position) => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
        </select>
      </label>
      <div className="addstaff__actions">
        <button
          type="submit"
          className="button button--primary"
          disabled={name.trim().length < 2 || add.isPending}
        >
          {add.isPending ? 'Завожу…' : twin ? 'Всё равно завести' : 'Завести'}
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => {
            setOpen(false);
            setProblem(null);
            setTwin(false);
          }}
        >
          Отмена
        </button>
      </div>
      {problem && <p className={`addstaff__note${twin ? ' addstaff__note--ask' : ''}`}>{problem}</p>}
    </form>
  );
}
